import type {
  InventoryRow,
  StockByWarehouseRow,
  WarehouseStockCell,
} from "@estelle/domain";

/**
 * One stock level, as far as the threshold form needs to know it (#521).
 *
 * Not an `InventoryRow`, because the editor is offered from two shapes of the
 * same stock: a flat row (Stock Levels, Low Stock) and a cell of the By
 * warehouse layout, which carries a level's figures and not its variant or its
 * warehouse. Each says what it is here, once, and the form never has to ask
 * which one it was handed.
 */
export interface ThresholdTarget {
  /** `InventoryRow.id`: one variant at one warehouse, which the route addresses. */
  levelId: string;
  product: string;
  /** The warehouse's code. */
  warehouse: string;
  sku: string | null;
  /**
   * The variant's own title, null for a product's only variant ("Default
   * Title" says nothing). It names the level where there is no SKU to (#548).
   */
  variant: string | null;
  available: number;
  /** The level's own threshold, or null when it has none. */
  own: number | null;
  /** What it is judged by: its own, else the store default. */
  effective: number;
}

function variantNameOf(title: string): string | null {
  return title === "" || title === "Default Title" ? null : title;
}

export function thresholdTargetOfRow(row: InventoryRow): ThresholdTarget {
  return {
    levelId: row.id,
    product: row.product.title,
    warehouse: row.warehouse.code,
    sku: row.variant.sku,
    variant: variantNameOf(row.variant.title),
    available: row.available,
    own: row.variant.lowStockThreshold,
    effective: row.effectiveLowStockThreshold,
  };
}

export function thresholdTargetOfCell(
  row: Pick<StockByWarehouseRow, "product" | "variant">,
  warehouse: string,
  cell: WarehouseStockCell,
): ThresholdTarget {
  return {
    levelId: cell.levelId,
    product: row.product.title,
    warehouse,
    sku: row.variant.sku,
    variant: variantNameOf(row.variant.title),
    available: cell.available,
    own: cell.lowStockThreshold,
    effective: cell.effectiveLowStockThreshold,
  };
}
